import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { ModuleAccessEditor, LEVEL_BADGE, MODULE_LEVELS, FEATURE_LEVELS } from "../../components/ModuleAccessEditor";
import { api, errMsg } from "../../lib/api";

const ROLE_HINTS = {
  supervisor: "Field-force manager: assigns properties and monitors collectors",
  tax_collector: "Mobile field agent: collections, surveys and receipts",
  citizen: "Public portal: payments, services and grievances",
};

// Phase 152: the server returns EFFECTIVE maps (module keys + "module.feature" keys). For editing we keep the raw
// form: feature keys that are not explicitly configured show as "inherit" (= Same as module).
const toEditable = (effective, configuredKeys) => {
  const set = new Set(configuredKeys || []);
  const out = {};
  Object.entries(effective || {}).forEach(([k, v]) => { out[k] = k.includes(".") && !set.has(k) ? "inherit" : v; });
  return out;
};

export default function RoleAccess() {
  const { code } = useParams();
  const [registry, setRegistry] = useState(null);
  const [config, setConfig] = useState({});
  const [customRoles, setCustomRoles] = useState([]);
  const [registryByRole, setRegistryByRole] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/t/${code}/role-modules`).then((r) => {
    // Municipal Admin access is managed by the platform Super Admin only.
    setRegistry(r.data.registry.filter((x) => x.role !== "municipal_admin"));
    setRegistryByRole(Object.fromEntries(r.data.registry.map((x) => [x.role, x.modules])));
    setConfig(Object.fromEntries(Object.entries(r.data.config).map(([role, eff]) => [role, toEditable(eff, r.data.configured?.[role])])));
    setCustomRoles((r.data.custom_roles || []).map((cr) => ({ ...cr, modules: toEditable(cr.modules, cr.configured) })));
  }), [code]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const setLevel = (role, key, val) =>
    setConfig((c) => ({ ...c, [role]: { ...c[role], [key]: val } }));

  const setCustomLevel = (roleId, key, val) =>
    setCustomRoles((rs) => rs.map((r) => r.id === roleId ? { ...r, modules: { ...r.modules, [key]: val } } : r));

  const save = async () => {
    setBusy(true);
    try {
      const { municipal_admin, ...editable } = config;
      const customPayload = Object.fromEntries(customRoles.map((r) => [r.id, r.modules]));
      await api.put(`/t/${code}/role-modules`, { config: editable, custom_roles: customPayload });
      await load();   // reload the effective + configured maps (feature keys that inherit stay "Same as module")
      toast.success("Role access saved — users will see the change on their next page load");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!registry) return <Spinner />;

  return (
    <div>
      <PageHeader title="Role Access" subtitle="Choose what each role can see and do in this municipality — per module and, inside each module, per sub-tab / feature">
        <Button onClick={save} disabled={busy} data-testid="role-access-save-button">
          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving..." : "Save changes"}
        </Button>
      </PageHeader>

      {/* Two columns at most — each card needs room for "sub-feature name + effective badge + level picker" on one line */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="role-access-grid">
        {registry.map((r) => (
          <Card key={r.role} className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" /> {r.role_label}
              </CardTitle>
              <p className="text-xs text-muted-foreground">{ROLE_HINTS[r.role]}</p>
            </CardHeader>
            <CardContent>
              {/* Phase 152: module level + expandable sub-feature levels (No access / View only / Full access / Same as module) */}
              <ModuleAccessEditor modules={r.modules} mode="role" getValue={(k) => config[r.role]?.[k]}
                onChange={(k, v) => setLevel(r.role, k, v)} testId={`role-module-${r.role}`} compact />
            </CardContent>
          </Card>
        ))}

        {/* Phase 71: tenant custom roles — configurable right here */}
        {customRoles.map((r) => (
          <Card key={r.id} className={`shadow-sm border-primary/30 ${r.is_active === false ? "opacity-70" : ""}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" /> {r.name}
                <Badge variant="outline" className="text-[10px]">Custom</Badge>
                {r.is_active === false && <Badge variant="outline" className="text-[10px] text-muted-foreground">Inactive</Badge>}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {r.description || `Custom role on the ${r.base_role_label} portal`} · {r.user_count} staff
              </p>
            </CardHeader>
            <CardContent data-testid={`role-access-custom-${r.id}`}>
              <ModuleAccessEditor modules={registryByRole[r.base_role] || []} mode="custom" getValue={(k) => r.modules?.[k]}
                onChange={(k, v) => setCustomLevel(r.id, k, v)} testId={`role-module-custom-${r.id}`} compact />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-4" data-testid="role-access-legend">
        <span>Module levels:</span>
        {MODULE_LEVELS.map((l) => <Badge key={l.value} variant="outline" className={`text-[11px] ${LEVEL_BADGE[l.value]}`}>{l.label}</Badge>)}
        <span className="ml-2">Sub-feature levels:</span>
        {FEATURE_LEVELS.map((l) => <Badge key={l.value} variant="outline" className={`text-[11px] ${LEVEL_BADGE[l.value]}`}>{l.label}</Badge>)}
        <Badge variant="outline" className="text-[11px]">Same as module</Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        "No access" hides the module (or the sub-tab) from the role's navigation and blocks its APIs; "View only" allows reading but no
        changes; "View &amp; edit" (modules only) allows updates but not create/delete. Expand a module to control its sub-tabs and
        features individually — they follow the module unless set. Custom roles are created under Master Data → Staff Roles.
        Per-user exceptions and Municipal Admin access are managed by the platform Super Admin under Access Control.
      </p>
    </div>
  );
}
